import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines class names using clsx and tailwind-merge utilities
 * @param inputs - Class values to combine
 * @returns Combined class name string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Utilities for data validation

/**
 * Checks if a value is a valid non-negative number
 * @param value - Value to validate (string or number)
 * @returns True if value is a valid non-negative number
 */
export function isValidNumber(value: string | number): boolean {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return !Number.isNaN(num) && Number.isFinite(num) && num >= 0;
}

// Utilities for formatting

/**
 * Formats a number as CO2 emission string
 * @param value - CO2 value in kg
 * @returns Formatted CO2 string (e.g., "1,234 kg CO₂")
 */
export function formatCO2(value: number): string {
  return `${Math.round(value).toLocaleString("fr-FR")} kg CO₂`;
}

/**
 * Formats a number as cost string in euros
 * @param value - Cost value in euros
 * @returns Formatted cost string (e.g., "1,234 €")
 */
export function formatCost(value: number): string {
  return `${Math.round(value).toLocaleString("fr-FR")} €`;
}

// Utilities for localStorage

/**
 * Retrieves a value from localStorage with JSON parsing
 * @param key - Storage key
 * @param defaultValue - Default value if key doesn't exist or is invalid
 * @returns Parsed value or default
 */
export function getStoredValue<T>(key: string, defaultValue: T): T {
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Stores a value in localStorage with JSON stringification
 * @param key - Storage key
 * @param value - Value to store
 */
export function setStoredValue<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore localStorage errors
  }
}
